import { ButtonHTMLAttributes, FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import { SIZE } from '@/types/common';
import { withOpacity } from '@/utils/opacity';

export const BOX_BUTTON_VARIANT = {
  SOLID: 'solid',
  OUTLINE: 'outline',
} as const;
export type BOX_BUTTON_VARIANT = (typeof BOX_BUTTON_VARIANT)[keyof typeof BOX_BUTTON_VARIANT];

export const BOX_BUTTON_SIZE = {
  ...SIZE,
  XLARGE: 'xlarge',
  XSMALL: 'xsmall',
} as const;
export type BOX_BUTTON_SIZE = (typeof BOX_BUTTON_SIZE)[keyof typeof BOX_BUTTON_SIZE];

export const BOX_BUTTON_COLOR = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  DANGER: 'danger',
  WARNING: 'warning',
  DEFAULT: 'default',
} as const;
export type BOX_BUTTON_COLOR = (typeof BOX_BUTTON_COLOR)[keyof typeof BOX_BUTTON_COLOR];

export interface BoxButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BOX_BUTTON_VARIANT;
  size?: BOX_BUTTON_SIZE;
  color?: BOX_BUTTON_COLOR;
  isCircle?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  full?: boolean;
  children: ReactNode;
}

/**
 * @deprecated
 */

const BoxButton: FunctionComponent<BoxButtonProps> = ({
  type = 'button',
  variant = BOX_BUTTON_VARIANT.SOLID,
  size = BOX_BUTTON_SIZE.MEDIUM,
  color = BOX_BUTTON_COLOR.PRIMARY,
  isCircle,
  leftIcon,
  rightIcon,
  disabled,
  onClick,
  full,
  children,
  ...props
}) => {
  return (
    <BoxButtonStyled
      {...props}
      type={type}
      variant={variant}
      size={size}
      color={color}
      isCircle={isCircle}
      disabled={disabled}
      onClick={onClick}
      full={full}
    >
      {leftIcon && <BoxButtonLeftIconWrapper size={size}>{leftIcon}</BoxButtonLeftIconWrapper>}
      {children}
      {rightIcon && <BoxButtonRightIconWrapper size={size}>{rightIcon}</BoxButtonRightIconWrapper>}
    </BoxButtonStyled>
  );
};

export default BoxButton;

export const BoxButtonStyled = styled(DefaultButtonStyled)<{
  variant: BOX_BUTTON_VARIANT;
  size: BOX_BUTTON_SIZE;
  color: BOX_BUTTON_COLOR;
  isCircle?: boolean;
  disabled?: boolean;
  full?: boolean;
}>`
  ${({ full }) => full && 'width: 100%'};
  ${({ size, theme }) =>
    size === BOX_BUTTON_SIZE.XLARGE
      ? `${theme.typography.UI16SB}; padding: 17px 16px`
      : size === BOX_BUTTON_SIZE.LARGE
        ? `${theme.typography.UI15M}; padding: 12px 16px`
        : size === BOX_BUTTON_SIZE.SMALL
          ? `${theme.typography.UI15M}; padding: 6px 12px`
          : size === BOX_BUTTON_SIZE.MEDIUM
            ? `${theme.typography.UI15R}; padding: 9px 20px`
            : size === BOX_BUTTON_SIZE.XSMALL
              ? `${theme.typography.UI13M}; padding: 6px 12px`
              : `${theme.typography.UI15M}; padding: 8px 12px`};
  ${({ isCircle }) => (!isCircle ? 'border-radius: 12px' : 'border-radius: 18px')};
  ${({ disabled, variant, color, theme }) =>
    variant === BOX_BUTTON_VARIANT.OUTLINE
      ? disabled
        ? color === BOX_BUTTON_COLOR.DANGER
          ? `border: 1px solid #efefef; background-color: ${theme.color.white}; color: ##ff303066`
          : color === BOX_BUTTON_COLOR.WARNING
            ? `border: 1px solid #efefef; background-color: ${
                theme.color.white
              }; color: ${withOpacity('#5992ff', '40%')}`
            : `border: 1px solid #efefef; background-color: ${theme.color.white}; color: #e4e4e4`
        : color === BOX_BUTTON_COLOR.DANGER
          ? `border: 1px solid #e4e4e4; background-color: ${theme.color.white}; color: #ff3030`
          : color === BOX_BUTTON_COLOR.WARNING
            ? `border: 1px solid #e4e4e4; background-color: ${theme.color.white}; color: #5992ff`
            : `border: 1px solid #e4e4e4; background-color: ${theme.color.white}; color: ${theme.color.black}`
      : disabled
        ? color === BOX_BUTTON_COLOR.SECONDARY
          ? 'solid #f7f7f7; background-color: #f7f7f7; color: #e4e4e4'
          : color === BOX_BUTTON_COLOR.TERTIARY
            ? 'border: 1px solid #f7f7f7; background-color: #f7f7f7; color: #e4e4e4'
            : color === BOX_BUTTON_COLOR.DANGER
              ? `border: 1px solid #ff3030; background-color: #ff30301a; color: ${theme.color.white}`
              : 'border: 1px solid #efefef; background-color: #efefef; color: #d8d8d8'
        : color === BOX_BUTTON_COLOR.SECONDARY
          ? `solid #f1f1f1; background-color: ${theme.color.primary100}; color: ${theme.color.primary700}`
          : color === BOX_BUTTON_COLOR.TERTIARY
            ? `border: 1px solid #f7f7f7; background-color: #f7f7f7; color: ${theme.color.black}`
            : color === BOX_BUTTON_COLOR.DANGER
              ? `border: 1px solid #ff3030; background-color: #ff3030b3; color: ${theme.color.white}`
              : `border: 1px solid ${theme.color.primary500}; background-color: ${theme.color.primary500}; color: ${theme.color.white}`};
      white-space: nowrap;
  /* Smartphones and touchscreens */
  @media (hover: none) and (pointer: coarse) {
    &:active {
      ${({ disabled, variant }) =>
        !disabled && variant === BOX_BUTTON_VARIANT.OUTLINE ? 'background-color: #f7f7f7' : ''};
    }
  }

  /* mouse, touch pad */
  @media (hover: hover) and (pointer: fine) {
    &:active {
      ${({ disabled, variant }) =>
        !disabled && variant === BOX_BUTTON_VARIANT.OUTLINE ? 'background-color: #f7f7f7' : ''};
    }
`;

export const BoxButtonLeftIconWrapper = styled.span<{ size: BOX_BUTTON_SIZE }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ size }) => (size === BOX_BUTTON_SIZE.XLARGE || size === BOX_BUTTON_SIZE.LARGE ? '4px' : '2px')};
`;

export const BoxButtonRightIconWrapper = styled.span<{ size: BOX_BUTTON_SIZE }>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ size }) =>
    size === BOX_BUTTON_SIZE.XLARGE || size === BOX_BUTTON_SIZE.LARGE
      ? 'width: 18px; height: 18px; margin-left: 4px'
      : size === BOX_BUTTON_SIZE.MEDIUM
        ? 'width: 14px; height: 14px; margin-left: 4px'
        : 'width: 14px; height: 14px; margin-left: 2px'};
`;

export const BoxButtonText = styled.span`
  ${({ theme }) => theme.typography.UI15R};
  color: ${({ theme }) => theme.color.black};
`;
